/**
 * useCustomFields — fetches active custom-field definitions for a DocType.
 *
 * Usage in a form:
 *   const { fields, loading, refetch } = useCustomFields('vendor');
 *   <DynamicFieldsSection fields={fields} values={customData} onChange={setCustomData} />
 */
import { useState, useEffect, useCallback } from 'react';
import { customFieldsApi } from '../services/customFieldsApi';
import logger from '../utils/logger';

export const useCustomFields = (doctype, { activeOnly = true, listViewOnly = false } = {}) => {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetch = useCallback(async () => {
    if (!doctype) {
      setFields([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await customFieldsApi.list({
        doctype,
        active_only: activeOnly,
      });
      const list = res.data || [];
      setFields(listViewOnly ? list.filter((f) => f.in_list_view) : list);
      setError(null);
    } catch (err) {
      logger.error('[useCustomFields] failed to load', err);
      setError(err);
      setFields([]);
    } finally {
      setLoading(false);
    }
  }, [doctype, activeOnly, listViewOnly]);

  useEffect(() => { fetch(); }, [fetch]);

  return { fields, loading, error, refetch: fetch };
};

export default useCustomFields;
